import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import EmployeeLayout from "@/components/EmployeeLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import {
  Package, ShoppingCart, Warehouse, Plus, Trash2, Send, DollarSign, Ship, Building2,
  AlertTriangle, Search, Eye, ChevronRight, Clock, CheckCircle2, XCircle, TrendingUp,
  FileText, BarChart3, Filter, ArrowUpDown, Truck, ClipboardList, RefreshCw,
  Shield, UserCheck, Gavel, Link2
} from "lucide-react";
import { format } from "date-fns";
import {
  getApprovalTier,
  getApprovalChain,
  formatValueRange,
  canApprove,
  EMPLOYEE_ROLE_TO_ACOC,
  ACOC_ROLE_LABELS,
  type AcocRole,
  type ApprovalTier as ApprovalTierType,
} from "@/data/approvalChainOfCustody";
import { toast } from "sonner";

// ─── Status & Priority Config ─────────────────────────────────────────────
const STATUS_CONFIG: Record<string, { label: string; color: string; icon: any; step: number }> = {
  draft: { label: "Draft", color: "bg-muted text-muted-foreground", icon: FileText, step: 0 },
  submitted: { label: "Submitted", color: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300", icon: Send, step: 1 },
  pending_approval: { label: "Pending Approval", color: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300", icon: Clock, step: 2 },
  approved: { label: "Approved", color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300", icon: CheckCircle2, step: 3 },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700 dark:bg-destructive/20 dark:text-red-300", icon: XCircle, step: -1 },
  ordered: { label: "Ordered", color: "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300", icon: Truck, step: 4 },
  delivered: { label: "Delivered", color: "bg-teal-100 text-teal-700 dark:bg-secondary/20 dark:text-secondary", icon: Package, step: 5 },
  completed: { label: "Completed", color: "bg-green-100 text-green-700 dark:bg-accent/20 dark:text-accent", icon: CheckCircle2, step: 6 },
  cancelled: { label: "Cancelled", color: "bg-muted text-muted-foreground", icon: XCircle, step: -1 },
};

const PRIORITY_CONFIG: Record<string, { label: string; color: string }> = {
  low: { label: "Low", color: "bg-muted text-muted-foreground" },
  normal: { label: "Normal", color: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300" },
  high: { label: "High", color: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300" },
  urgent: { label: "Urgent", color: "bg-red-100 text-red-700 dark:bg-destructive/20 dark:text-red-300" },
};

interface LineItem {
  item_description: string;
  part_number: string;
  quantity: number;
  unit: string;
  unit_price: number;
}

// ─── Status Pipeline Component ────────────────────────────────────────────
const StatusPipeline = ({ currentStatus }: { currentStatus: string }) => {
  const steps = ["draft", "submitted", "pending_approval", "approved", "ordered", "delivered", "completed"];
  const currentStep = STATUS_CONFIG[currentStatus]?.step ?? 0;
  const isTerminal = currentStatus === "rejected" || currentStatus === "cancelled";

  if (isTerminal) {
    const Icon = STATUS_CONFIG[currentStatus].icon;
    return (
      <div className="flex items-center gap-2 text-sm text-destructive">
        <Icon className="w-4 h-4" />
        <span className="font-medium">{STATUS_CONFIG[currentStatus].label}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {steps.map((step, i) => {
        const isActive = i <= currentStep;
        const isCurrent = i === currentStep;
        return (
          <div key={step} className="flex items-center gap-1">
            <div
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                isCurrent ? "bg-secondary ring-2 ring-secondary/30 scale-125" : isActive ? "bg-secondary/60" : "bg-muted"
              }`}
              title={STATUS_CONFIG[step].label}
            />
            {i < steps.length - 1 && (
              <div className={`w-4 h-0.5 ${isActive && i < currentStep ? "bg-secondary/60" : "bg-muted"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
};


const ApprovalChainDisplay = ({ order }: { order: any }) => {
  const est = Number(order.estimated_total || 0);
  const tier = getApprovalTier(est, order.order_type, order.scope);
  const chain = getApprovalChain(tier, order.status, order.approved_by);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-secondary" />
          <span className="text-xs font-bold uppercase tracking-wider text-foreground">Chain of Custody</span>
        </div>
        <Badge variant="outline" className="text-[10px] font-mono border-secondary/30 text-secondary">
          {tier.id} — {formatValueRange(tier)}
        </Badge>
      </div>

      {/* Approval pipeline */}
      <div className="relative pl-4 space-y-2">
        <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-border" />
        {chain.map((step, i) => {
          const isApproved = step.status === "approved";
          const isCurrent = step.status === "current";
          return (
            <div key={i} className="relative flex items-start gap-3">
              <div className={`absolute -left-4 top-1 w-3 h-3 rounded-full border-2 z-10 ${
                isApproved ? "bg-emerald-500 border-emerald-500" :
                isCurrent ? "bg-secondary border-secondary animate-pulse" :
                "bg-muted border-border"
              }`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold ${isApproved ? "text-emerald-600 dark:text-emerald-400" : isCurrent ? "text-secondary" : "text-muted-foreground"}`}>
                    {step.label}
                  </span>
                  {isApproved && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
                  {isCurrent && <Clock className="w-3 h-3 text-secondary" />}
                </div>
                <p className="text-[10px] text-muted-foreground">
                  {step.type === "finance" ? "Budget availability gate" :
                   step.type === "approver1" ? "Primary approval authority" :
                   step.type === "approver2" ? "Countersign / oversight" : "Review"}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* LLCA Reference */}
      <div className="bg-muted/50 rounded-lg p-2.5 border border-border">
        <div className="flex items-center gap-1.5 mb-1">
          <Gavel className="w-3 h-3 text-muted-foreground" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">LLCA Authority</span>
        </div>
        <p className="text-[11px] text-muted-foreground leading-relaxed">{tier.llcaNote}</p>
      </div>

      {tier.requiresQES && (
        <p className="text-[10px] text-amber-600 dark:text-amber-400 flex items-center gap-1">
          <FileText className="w-3 h-3" />Quotation Evaluation Summary (QES) required for this tier
        </p>
      )}
    </div>
  );
};

// ─── Order Detail Panel ───────────────────────────────────────────────────
const OrderDetailPanel = ({
  order, onClose, user, onSubmit, employeeRole, onApprove, onReject
}: {
  order: any; onClose: () => void; user: any; onSubmit: (id: string) => void;
  employeeRole: string; onApprove?: (id: string, notes: string) => void; onReject?: (id: string, notes: string) => void;
}) => {
  const [approvalNotes, setApprovalNotes] = useState("");
  const { data: lineItems = [] } = useQuery({
    queryKey: ["po-items", order.id],
    queryFn: async () => {
      const { data, error } = await supabase.from("purchase_order_items").select("*").eq("order_id", order.id).order("created_at");
      if (error) throw error;
      return data;
    },
  });

  const StatusIcon = STATUS_CONFIG[order.status]?.icon || FileText;
  const est = Number(order.estimated_total || 0);
  const tier = getApprovalTier(est, order.order_type, order.scope);
  const userAcocRole = EMPLOYEE_ROLE_TO_ACOC[employeeRole] || "vessel_crew";
  const approvalAuthority = canApprove(userAcocRole, tier);
  const canAct = order.status === "pending_approval" && approvalAuthority;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="gradient-navy rounded-xl p-6 relative overflow-hidden">
        <div className="grid-overlay absolute inset-0 pointer-events-none opacity-30" />
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <code className="text-sm font-mono text-secondary bg-secondary/10 px-3 py-1 rounded-lg">{order.order_number}</code>
              <Badge variant="outline" className="text-[10px] font-mono border-primary-foreground/20 text-primary-foreground/60">
                {tier.id}
              </Badge>
            </div>
            <Badge className={STATUS_CONFIG[order.status]?.color || ""}>{STATUS_CONFIG[order.status]?.label || order.status}</Badge>
          </div>
          <h2 className="text-xl font-bold text-primary-foreground mb-1">{order.title}</h2>
          <p className="text-sm text-primary-foreground/60">{order.description || "No description provided"}</p>
        </div>
      </div>

      {/* Status Pipeline */}
      <div>
        <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Order Progress</Label>
        <StatusPipeline currentStatus={order.status} />
      </div>

      {/* ACOC Approval Chain */}
      <ApprovalChainDisplay order={order} />

      {/* Key Details Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { label: "Scope", value: order.scope === "vessel" ? `Vessel — ${order.vessel_name}` : "Corporate", icon: order.scope === "vessel" ? Ship : Building2 },
          { label: "Type", value: order.order_type === "material" ? "Material Order" : "Service Order", icon: Package },
          { label: "Priority", value: PRIORITY_CONFIG[order.priority]?.label || order.priority, icon: AlertTriangle },
          { label: "Vendor", value: order.vendor_name || "—", icon: Truck },
          { label: "Requested By", value: order.requested_by_name || "—", icon: ClipboardList },
          { label: "Created", value: format(new Date(order.created_at), "MMM d, yyyy"), icon: Clock },
        ].map((item) => (
          <div key={item.label} className="bg-muted/50 rounded-lg p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <item.icon className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{item.label}</span>
            </div>
            <p className="text-sm font-semibold text-foreground">{item.value}</p>
          </div>
        ))}
      </div>

      {/* Financial Summary */}
      <div className="bg-secondary/5 border border-secondary/20 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Estimated Total</p>
            <p className="text-2xl font-bold text-foreground">${est.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Approval Tier</p>
            <code className="text-sm font-mono text-secondary font-bold">{tier.id}</code>
            <p className="text-[10px] text-muted-foreground">{formatValueRange(tier)}</p>
          </div>
        </div>
        {order.budget_code && (
          <div className="mt-2 pt-2 border-t border-secondary/10">
            <p className="text-xs text-muted-foreground">Budget Code</p>
            <code className="text-sm font-mono text-foreground">{order.budget_code}</code>
          </div>
        )}
      </div>

      {/* Line Items Table */}
      <div>
        <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Line Items ({lineItems.length})</Label>
        {lineItems.length > 0 ? (
          <div className="border border-border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="text-xs">Description</TableHead>
                  <TableHead className="text-xs">Part #</TableHead>
                  <TableHead className="text-xs text-right">Qty</TableHead>
                  <TableHead className="text-xs text-right">Unit Price</TableHead>
                  <TableHead className="text-xs text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lineItems.map((li: any) => (
                  <TableRow key={li.id}>
                    <TableCell className="text-sm font-medium">{li.item_description}</TableCell>
                    <TableCell className="text-sm font-mono text-muted-foreground">{li.part_number || "—"}</TableCell>
                    <TableCell className="text-sm text-right">{li.quantity} {li.unit}</TableCell>
                    <TableCell className="text-sm text-right">${Number(li.unit_price || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-sm text-right font-semibold">${Number(li.total_price || 0).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-4 text-center">No line items</p>
        )}
      </div>

      {/* Delivery Info */}
      {(order.expected_delivery || order.delivery_address) && (
        <div className="grid grid-cols-2 gap-4">
          {order.expected_delivery && (
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Expected Delivery</p>
              <p className="text-sm font-semibold text-foreground">{format(new Date(order.expected_delivery), "MMM d, yyyy")}</p>
            </div>
          )}
          {order.delivery_address && (
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Delivery Address</p>
              <p className="text-sm text-foreground">{order.delivery_address}</p>
            </div>
          )}
        </div>
      )}

      {/* Approval History */}
      {order.approved_by_name && (
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
            {order.status === "rejected" ? "Rejected By" : "Approved By"}
          </p>
          <p className="text-sm font-semibold text-foreground">{order.approved_by_name}</p>
          {order.approval_notes && <p className="text-xs text-muted-foreground mt-1">{order.approval_notes}</p>}
        </div>
      )}

      {/* Notes */}
      {order.notes && (
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Notes</p>
          <p className="text-sm text-foreground">{order.notes}</p>
        </div>
      )}

      {/* Actions: Submit Draft */}
      {order.status === "draft" && order.requested_by === user?.id && (
        <Button onClick={() => onSubmit(order.id)} className="w-full gap-2 bg-secondary hover:bg-secondary/90 text-secondary-foreground">
          <Send className="w-4 h-4" />Submit for Processing
        </Button>
      )}

      {/* Actions: Approve / Reject (role-gated) */}
      {canAct && onApprove && onReject && (
        <div className="border-t border-border pt-4 space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <UserCheck className="w-4 h-4 text-secondary" />
            <span className="text-xs font-bold uppercase tracking-wider text-foreground">Your Approval Authority</span>
            <Badge variant="outline" className="text-[10px] ml-auto">{ACOC_ROLE_LABELS[userAcocRole]}</Badge>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Approval Notes</Label>
            <Textarea
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
              placeholder="Optional notes for audit trail..."
              className="h-16 text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Button
              onClick={() => onApprove(order.id, approvalNotes)}
              className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <CheckCircle2 className="w-4 h-4" />Approve
            </Button>
            <Button
              onClick={() => onReject(order.id, approvalNotes)}
              variant="destructive"
              className="gap-2"
            >
              <XCircle className="w-4 h-4" />Reject
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────
const EmployeeSupplyChain = () => {
  const { user, fullName, employeePermissions } = useAuth();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailOrder, setDetailOrder] = useState<any>(null);
  const [tab, setTab] = useState("overview");
  const [filterScope, setFilterScope] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<"created_at" | "estimated_total">("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  // Form state
  const [orderType, setOrderType] = useState("material");
  const [scope, setScope] = useState("corporate");
  const [vesselId, setVesselId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [priority, setPriority] = useState("normal");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [expectedDelivery, setExpectedDelivery] = useState("");
  const [budgetCode, setBudgetCode] = useState("");
  const [notes, setNotes] = useState("");
  const [lineItems, setLineItems] = useState<LineItem[]>([{ item_description: "", part_number: "", quantity: 1, unit: "ea", unit_price: 0 }]);

  // Inventory form
  const [invOpen, setInvOpen] = useState(false);
  const [invName, setInvName] = useState("");
  const [invPartNum, setInvPartNum] = useState("");
  const [invCategory, setInvCategory] = useState("general");
  const [invLocType, setInvLocType] = useState("warehouse");
  const [invVesselId, setInvVesselId] = useState("");
  const [invQty, setInvQty] = useState(0);
  const [invMinQty, setInvMinQty] = useState(0);
  const [invUnit, setInvUnit] = useState("ea");

  // ─── Queries ────────────────────────────────────────────────────────────
  const { data: orders = [], isLoading: ordersLoading } = useQuery({
    queryKey: ["purchase-orders"],
    queryFn: async () => {
      const { data, error } = await supabase.from("purchase_orders").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: vessels = [] } = useQuery({
    queryKey: ["fleet-vessels-sc"],
    queryFn: async () => {
      const { data, error } = await supabase.from("fleet_vessels").select("id, name").order("name");
      if (error) throw error;
      return data;
    },
  });

  const { data: vendors = [] } = useQuery({
    queryKey: ["vendors-sc"],
    queryFn: async () => {
      const { data, error } = await supabase.from("vendors").select("id, name").order("name");
      if (error) throw error;
      return data;
    },
  });

  const { data: inventory = [], isLoading: invLoading } = useQuery({
    queryKey: ["inventory-items"],
    queryFn: async () => {
      const { data, error } = await supabase.from("inventory_items").select("*").order("name");
      if (error) throw error;
      return data;
    },
  });

  // ─── Mutations ──────────────────────────────────────────────────────────
  const createOrder = useMutation({
    mutationFn: async () => {
      const vesselName = scope === "vessel" ? vessels.find((v) => v.id === vesselId)?.name || "" : null;
      const vendorName = vendorId ? vendors.find((v) => v.id === vendorId)?.name || "" : null;
      const { data: orderNum, error: numErr } = await supabase.rpc("generate_order_number", {
        p_order_type: orderType, p_scope: scope, p_vessel_name: vesselName,
      });
      if (numErr) throw numErr;
      const estimatedTotal = lineItems.reduce((sum, li) => sum + li.quantity * li.unit_price, 0);
      const { data: po, error: poErr } = await supabase.from("purchase_orders").insert({
        order_number: orderNum, order_type: orderType, scope,
        vessel_id: scope === "vessel" ? vesselId : null, vessel_name: vesselName,
        title, description, vendor_id: vendorId || null, vendor_name: vendorName,
        priority, requested_by: user!.id, requested_by_name: fullName || user!.email,
        estimated_total: estimatedTotal, delivery_address: deliveryAddress || null,
        expected_delivery: expectedDelivery || null, budget_code: budgetCode || null, notes: notes || null,
      }).select("id").single();
      if (poErr) throw poErr;
      const items = lineItems.filter((li) => li.item_description.trim()).map((li) => ({
        order_id: po.id, item_description: li.item_description, part_number: li.part_number || null,
        quantity: li.quantity, unit: li.unit, unit_price: li.unit_price, total_price: li.quantity * li.unit_price,
      }));
      if (items.length > 0) {
        const { error: itemErr } = await supabase.from("purchase_order_items").insert(items);
        if (itemErr) throw itemErr;
      }
      return po;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
      toast.success("Order created", { description: "Purchase order saved as draft." });
      resetForm();
      setCreateOpen(false);
    },
    onError: (err: any) => toast.error("Error", { description: err.message }),
  });

  const submitOrder = useMutation({
    mutationFn: async (orderId: string) => {
      const order = orders.find((o) => o.id === orderId);
      const est = Number(order?.estimated_total || 0);
      const tier = getApprovalTier(est, (order?.order_type || "material") as "material" | "service", (order?.scope || "corporate") as "corporate" | "vessel");
      // A1 tier (up to $2,500) auto-approves for tech_supt+ roles
      const userAcocRole = EMPLOYEE_ROLE_TO_ACOC[employeePermissions.employee_role] || "vessel_crew";
      const autoApprove = tier.id === "A1" && (userAcocRole === "tech_supt" || userAcocRole === "coo" || userAcocRole === "ceo");
      const newStatus = autoApprove ? "approved" : "pending_approval";
      const updateData: any = { status: newStatus, updated_at: new Date().toISOString() };
      if (autoApprove) {
        updateData.approved_by = user!.id;
        updateData.approved_by_name = fullName || user!.email;
        updateData.approval_notes = `Auto-approved (${tier.id} — ${ACOC_ROLE_LABELS[userAcocRole]})`;
      }
      const { error } = await supabase.from("purchase_orders").update(updateData).eq("id", orderId);
      if (error) throw error;
      return { status: newStatus, tier };
    },
    onSuccess: ({ status, tier }) => {
      queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
      toast.success(
        status === "pending_approval" ? `Routed to ${tier.approver1Label}` : "Order Auto-Approved",
        {
          description: status === "pending_approval"
            ? `${tier.id}: ${formatValueRange(tier)} — requires ${tier.approver1Label} approval per VO-QMS-ACOC-001.`
            : `${tier.id} order auto-approved (within your authority).`,
        }
      );
      setDetailOrder(null);
    },
    onError: (err: any) => toast.error("Error", { description: err.message }),
  });

  const approveOrder = useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes: string }) => {
      const { error } = await supabase.from("purchase_orders").update({
        status: "approved",
        approved_by: user!.id,
        approved_by_name: fullName || user!.email,
        approval_notes: notes || null,
        updated_at: new Date().toISOString(),
      }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
      toast.success("Order Approved", { description: "Purchase order has been approved per ACOC chain of custody." });
      setDetailOrder(null);
    },
    onError: (err: any) => toast.error("Error", { description: err.message }),
  });

  const rejectOrder = useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes: string }) => {
      if (!notes.trim() || notes.trim().length < 20) throw new Error("Rejection reason must be at least 20 characters (per VO-QMS-ACOC-001).");
      const { error } = await supabase.from("purchase_orders").update({
        status: "rejected",
        approved_by: user!.id,
        approved_by_name: fullName || user!.email,
        approval_notes: notes,
        updated_at: new Date().toISOString(),
      }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
      toast.success("Order Rejected", { description: "Purchase order has been rejected with documented reason." });
      setDetailOrder(null);
    },
    onError: (err: any) => toast.error("Error", { description: err.message }),
  });

  const createInventoryItem = useMutation({
    mutationFn: async () => {
      const vName = invLocType === "vessel" ? vessels.find((v) => v.id === invVesselId)?.name || "" : null;
      const { error } = await supabase.from("inventory_items").insert({
        name: invName, part_number: invPartNum || null, category: invCategory,
        location_type: invLocType, vessel_id: invLocType === "vessel" ? invVesselId : null,
        vessel_name: vName, quantity_on_hand: invQty, minimum_quantity: invMinQty, unit: invUnit,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventory-items"] });
      toast.success("Item Added");
      setInvOpen(false);
      setInvName(""); setInvPartNum(""); setInvCategory("general"); setInvLocType("warehouse"); setInvVesselId(""); setInvQty(0); setInvMinQty(0);
    },
    onError: (err: any) => toast.error("Error", { description: err.message }),
  });

  const resetForm = () => {
    setOrderType("material"); setScope("corporate"); setVesselId(""); setTitle(""); setDescription("");
    setVendorId(""); setPriority("normal"); setDeliveryAddress(""); setExpectedDelivery(""); setBudgetCode(""); setNotes("");
    setLineItems([{ item_description: "", part_number: "", quantity: 1, unit: "ea", unit_price: 0 }]);
  };

  const addLineItem = () => setLineItems([...lineItems, { item_description: "", part_number: "", quantity: 1, unit: "ea", unit_price: 0 }]);
  const removeLineItem = (i: number) => setLineItems(lineItems.filter((_, idx) => idx !== i));
  const updateLineItem = (i: number, field: keyof LineItem, value: any) => {
    const updated = [...lineItems];
    (updated[i] as any)[field] = value;
    setLineItems(updated);
  };

  // ─── Computed Data ──────────────────────────────────────────────────────
  const filteredOrders = useMemo(() => {
    let result = orders.filter((o) => {
      if (filterScope !== "all" && o.scope !== filterScope) return false;
      if (filterStatus !== "all" && o.status !== filterStatus) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return (
          o.title.toLowerCase().includes(term) ||
          o.order_number.toLowerCase().includes(term) ||
          (o.vendor_name || "").toLowerCase().includes(term) ||
          (o.vessel_name || "").toLowerCase().includes(term)
        );
      }
      return true;
    });
    result.sort((a, b) => {
      const aVal = sortField === "estimated_total" ? Number(a.estimated_total || 0) : new Date(a.created_at).getTime();
      const bVal = sortField === "estimated_total" ? Number(b.estimated_total || 0) : new Date(b.created_at).getTime();
      return sortDir === "desc" ? bVal - aVal : aVal - bVal;
    });
    return result;
  }, [orders, filterScope, filterStatus, searchTerm, sortField, sortDir]);

  const activeStatuses = ["approved", "ordered", "delivered", "completed"];
  const totalSpend = orders.filter((o) => activeStatuses.includes(o.status)).reduce((sum, o) => sum + Number(o.estimated_total || 0), 0);
  const corporateSpend = orders.filter((o) => o.scope === "corporate" && activeStatuses.includes(o.status)).reduce((sum, o) => sum + Number(o.estimated_total || 0), 0);
  const vesselSpend = orders.filter((o) => o.scope === "vessel" && activeStatuses.includes(o.status)).reduce((sum, o) => sum + Number(o.estimated_total || 0), 0);
  const lowStockItems = inventory.filter((i) => Number(i.quantity_on_hand) <= Number(i.minimum_quantity));
  const pendingCount = orders.filter((o) => o.status === "pending_approval").length;
  const draftCount = orders.filter((o) => o.status === "draft").length;
  const orderedCount = orders.filter((o) => o.status === "ordered").length;

  const statusBreakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    orders.forEach((o) => { counts[o.status] = (counts[o.status] || 0) + 1; });
    return counts;
  }, [orders]);

  const toggleSort = (field: "created_at" | "estimated_total") => {
    if (sortField === field) setSortDir(sortDir === "desc" ? "asc" : "desc");
    else { setSortField(field); setSortDir("desc"); }
  };

  return (
    <EmployeeLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="gradient-navy rounded-2xl p-6 lg:p-8 relative overflow-hidden">
          <div className="grid-overlay absolute inset-0 pointer-events-none opacity-30" />
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <div className="section-label mb-3">Supply Chain Management</div>
              <h1 className="text-2xl lg:text-3xl font-bold text-primary-foreground mb-1">Procurement & Inventory</h1>
              <p className="text-sm text-primary-foreground/50 font-body">
                End-to-end purchase order management, inventory tracking, and vendor coordination
              </p>
            </div>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => toast.success("Created: New Purchase Order", { description: "New entry added." })} size="lg" className="gap-2 bg-secondary hover:bg-secondary/90 text-secondary-foreground shadow-lg">
                  <Plus className="w-5 h-5" />New Purchase Order
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Purchase Order</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Order Type</Label>
                      <Select value={orderType} onValueChange={setOrderType}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="material">Material Order (MO)</SelectItem>
                          <SelectItem value="service">Service Order (SO)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Scope</Label>
                      <Select value={scope} onValueChange={setScope}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="corporate">Corporate (CO-)</SelectItem>
                          <SelectItem value="vessel">Vessel-Specific (VO-)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {scope === "vessel" && (
                    <div>
                      <Label>Vessel</Label>
                      <Select value={vesselId} onValueChange={setVesselId}>
                        <SelectTrigger><SelectValue placeholder="Select vessel" /></SelectTrigger>
                        <SelectContent>
                          {vessels.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <Label>Title</Label>
                    <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Order title" />
                  </div>

                  <div>
                    <Label>Description</Label>
                    <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Order description" />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Vendor</Label>
                      <Select value={vendorId} onValueChange={setVendorId}>
                        <SelectTrigger><SelectValue placeholder="Select vendor" /></SelectTrigger>
                        <SelectContent>
                          {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Priority</Label>
                      <Select value={priority} onValueChange={setPriority}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="urgent">Urgent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Budget Code</Label>
                      <Input value={budgetCode} onChange={(e) => setBudgetCode(e.target.value)} />
                    </div>
                    <div>
                      <Label>Expected Delivery</Label>
                      <Input type="date" value={expectedDelivery} onChange={(e) => setExpectedDelivery(e.target.value)} />
                    </div>
                    <div>
                      <Label>Delivery Address</Label>
                      <Input value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} />
                    </div>
                  </div>

                  {/* Line Items */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Line Items</Label>
                      <Button variant="ghost" size="sm" onClick={addLineItem} className="gap-1 text-secondary"><Plus className="w-3 h-3" />Add Item</Button>
                    </div>
                    <div className="space-y-2 border border-border rounded-lg p-3 bg-muted/30">
                      <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-muted-foreground font-medium px-1">
                        <span className="col-span-4">Description</span>
                        <span className="col-span-2">Part #</span>
                        <span className="col-span-1">Qty</span>
                        <span className="col-span-1">Unit</span>
                        <span className="col-span-2">Price</span>
                        <span className="col-span-1 text-right">Total</span>
                        <span className="col-span-1"></span>
                      </div>
                      {lineItems.map((li, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-4">
                            <Input placeholder="Description" value={li.item_description} onChange={(e) => updateLineItem(i, "item_description", e.target.value)} className="text-xs h-9" />
                          </div>
                          <div className="col-span-2">
                            <Input placeholder="Part #" value={li.part_number} onChange={(e) => updateLineItem(i, "part_number", e.target.value)} className="text-xs h-9" />
                          </div>
                          <div className="col-span-1">
                            <Input type="number" value={li.quantity} onChange={(e) => updateLineItem(i, "quantity", Number(e.target.value))} className="text-xs h-9" />
                          </div>
                          <div className="col-span-1">
                            <Input value={li.unit} onChange={(e) => updateLineItem(i, "unit", e.target.value)} className="text-xs h-9" />
                          </div>
                          <div className="col-span-2">
                            <Input type="number" step="0.01" placeholder="Price" value={li.unit_price} onChange={(e) => updateLineItem(i, "unit_price", Number(e.target.value))} className="text-xs h-9" />
                          </div>
                          <div className="col-span-1 text-right text-xs font-semibold text-foreground">${(li.quantity * li.unit_price).toFixed(2)}</div>
                          <div className="col-span-1 flex justify-end">
                            {lineItems.length > 1 && <Button variant="ghost" size="icon" onClick={() => removeLineItem(i)} className="h-8 w-8 text-destructive"><Trash2 className="w-3 h-3" /></Button>}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="text-right mt-2 text-sm font-bold text-foreground">
                      Estimated Total: ${lineItems.reduce((s, li) => s + li.quantity * li.unit_price, 0).toFixed(2)}
                    </div>
                    {(() => {
                      const est = lineItems.reduce((s, li) => s + li.quantity * li.unit_price, 0);
                      const tier = getApprovalTier(est, orderType as "material" | "service", scope as "corporate" | "vessel");
                      return (
                        <p className="text-xs text-muted-foreground mt-1 text-right flex items-center justify-end gap-1">
                          <Shield className="w-3 h-3 text-secondary" />
                          <span className="font-mono font-bold text-secondary">{tier.id}</span> — {tier.approver1Label}
                          {tier.approver2Label && <span> + {tier.approver2Label}</span>}
                        </p>
                      );
                    })()}
                  </div>

                  <div>
                    <Label>Notes</Label>
                    <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
                  </div>

                  <Button onClick={() => createOrder.mutate()} disabled={!title.trim() || createOrder.isPending} className="w-full gap-2 bg-secondary hover:bg-secondary/90 text-secondary-foreground">
                    <Package className="w-4 h-4" />{createOrder.isPending ? "Creating..." : "Create Draft Order"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            { label: "Total Spend", value: `$${totalSpend.toLocaleString()}`, icon: DollarSign, color: "text-secondary" },
            { label: "Corporate", value: `$${corporateSpend.toLocaleString()}`, icon: Building2, color: "text-blue-500" },
            { label: "Vessel", value: `$${vesselSpend.toLocaleString()}`, icon: Ship, color: "text-accent" },
            { label: "Drafts", value: draftCount, icon: FileText, color: "text-muted-foreground" },
            { label: "Pending Approval", value: pendingCount, icon: Clock, color: "text-amber-500" },
            { label: "In Transit", value: orderedCount, icon: Truck, color: "text-indigo-500" },
            { label: "Low Stock", value: lowStockItems.length, icon: AlertTriangle, color: lowStockItems.length > 0 ? "text-amber-500" : "text-muted-foreground" },
          ].map((kpi) => (
            <Card key={kpi.label} className="border-border">
              <CardContent className="p-3 flex items-center gap-2.5">
                <kpi.icon className={`w-5 h-5 ${kpi.color} flex-shrink-0`} />
                <div className="min-w-0">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider truncate">{kpi.label}</p>
                  <p className="text-base font-bold text-foreground">{kpi.value}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-card border border-border">
            <TabsTrigger value="overview" className="gap-2"><BarChart3 className="w-4 h-4" />Overview</TabsTrigger>
            <TabsTrigger value="orders" className="gap-2"><ShoppingCart className="w-4 h-4" />Purchase Orders</TabsTrigger>
            <TabsTrigger value="approvals" className="gap-2">
              <Shield className="w-4 h-4" />Approvals
              {pendingCount > 0 && <Badge className="ml-1 bg-amber-500 text-white text-[10px] px-1.5 py-0 h-4">{pendingCount}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="inventory" className="gap-2"><Warehouse className="w-4 h-4" />Inventory</TabsTrigger>
          </TabsList>

          {/* ─── OVERVIEW TAB ───────────────────────────────────────────── */}
          <TabsContent value="overview" className="space-y-6 mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Status Breakdown */}
              <Card className="border-border">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2"><BarChart3 className="w-4 h-4 text-secondary" />Order Status Breakdown</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {Object.entries(STATUS_CONFIG).filter(([key]) => statusBreakdown[key]).map(([key, cfg]) => {
                    const count = statusBreakdown[key] || 0;
                    const pct = orders.length > 0 ? (count / orders.length) * 100 : 0;
                    return (
                      <div key={key} className="flex items-center gap-3">
                        <div className="w-28 text-xs font-medium text-foreground">{cfg.label}</div>
                        <div className="flex-1">
                          <Progress value={pct} className="h-2" />
                        </div>
                        <div className="w-8 text-xs text-right font-bold text-foreground">{count}</div>
                      </div>
                    );
                  })}
                  {orders.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No orders yet</p>}
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card className="border-border">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4 text-secondary" />Recent Orders</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {orders.slice(0, 5).map((o) => (
                    <button
                      key={o.id}
                      onClick={() => { setDetailOrder(o); setTab("orders"); }}
                      className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50 transition-colors text-left"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{o.title}</p>
                        <p className="text-xs text-muted-foreground">{o.order_number} · {format(new Date(o.created_at), "MMM d")}</p>
                      </div>
                      <Badge className={`${STATUS_CONFIG[o.status]?.color || ""} text-[10px]`}>{STATUS_CONFIG[o.status]?.label || o.status}</Badge>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </button>
                  ))}
                  {orders.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No orders yet</p>}
                </CardContent>
              </Card>

              {/* Low Stock Alerts */}
              <Card className={`border-border ${lowStockItems.length > 0 ? "border-amber-500/30" : ""}`}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" />Low Stock Alerts</CardTitle>
                </CardHeader>
                <CardContent>
                  {lowStockItems.length > 0 ? (
                    <div className="space-y-2">
                      {lowStockItems.slice(0, 6).map((item) => (
                        <div key={item.id} className="flex items-center justify-between p-2 rounded-lg bg-amber-500/5 border border-amber-500/10">
                          <div>
                            <p className="text-sm font-medium text-foreground">{item.name}</p>
                            <p className="text-xs text-muted-foreground">{item.location_type === "vessel" ? item.vessel_name : "Warehouse"} · {item.category}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-amber-600 dark:text-amber-400">{item.quantity_on_hand} {item.unit}</p>
                            <p className="text-[10px] text-muted-foreground">min: {item.minimum_quantity}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-muted-foreground">
                      <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                      <p className="text-sm">All inventory levels are healthy</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card className="border-border">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-secondary" />Quick Stats</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {[
                    { label: "Total Orders", value: orders.length },
                    { label: "Unique Vendors", value: new Set(orders.filter(o => o.vendor_name).map(o => o.vendor_name)).size },
                    { label: "Total Inventory Items", value: inventory.length },
                    { label: "Avg Order Value", value: orders.length > 0 ? `$${Math.round(totalSpend / Math.max(orders.filter(o => activeStatuses.includes(o.status)).length, 1)).toLocaleString()}` : "$0" },
                  ].map((stat) => (
                    <div key={stat.label} className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{stat.label}</span>
                      <span className="text-sm font-bold text-foreground">{stat.value}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ─── PURCHASE ORDERS TAB ────────────────────────────────────── */}
          <TabsContent value="orders" className="space-y-4 mt-4">
            {detailOrder ? (
              <div>
                <Button variant="ghost" onClick={() => setDetailOrder(null)} className="gap-2 mb-4 text-muted-foreground hover:text-foreground">
                  <ChevronRight className="w-4 h-4 rotate-180" />Back to Orders
                </Button>
                <Card className="border-border">
                  <CardContent className="p-6">
                    <OrderDetailPanel
                      order={detailOrder}
                      onClose={() => setDetailOrder(null)}
                      user={user}
                      onSubmit={(id) => submitOrder.mutate(id)}
                      employeeRole={employeePermissions.employee_role}
                      onApprove={(id, notes) => approveOrder.mutate({ id, notes })}
                      onReject={(id, notes) => rejectOrder.mutate({ id, notes })}
                    />
                  </CardContent>
                </Card>
              </div>
            ) : (
              <>
                {/* Search & Filters */}
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative flex-1 min-w-[200px] max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search orders, vendors, vessels..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 h-9"
                    />
                  </div>
                  <Select value={filterScope} onValueChange={setFilterScope}>
                    <SelectTrigger className="w-[130px] h-9"><Filter className="w-3 h-3 mr-1" /><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Scopes</SelectItem>
                      <SelectItem value="corporate">Corporate</SelectItem>
                      <SelectItem value="vessel">Vessel</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-[160px] h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                        <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-1 ml-auto">
                    <Button variant="ghost" size="sm" onClick={() => toggleSort("created_at")} className={`text-xs gap-1 ${sortField === "created_at" ? "text-foreground" : "text-muted-foreground"}`}>
                      <ArrowUpDown className="w-3 h-3" />Date
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => toggleSort("estimated_total")} className={`text-xs gap-1 ${sortField === "estimated_total" ? "text-foreground" : "text-muted-foreground"}`}>
                      <ArrowUpDown className="w-3 h-3" />Amount
                    </Button>
                  </div>
                </div>

                {/* Orders Table */}
                {ordersLoading ? (
                  <div className="text-center py-12 text-muted-foreground">Loading orders...</div>
                ) : filteredOrders.length === 0 ? (
                  <Card className="border-border">
                    <CardContent className="py-12 text-center">
                      <ShoppingCart className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
                      <p className="text-muted-foreground">No purchase orders found</p>
                      <p className="text-xs text-muted-foreground/60 mt-1">Try adjusting your filters or create a new order</p>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="border-border overflow-hidden">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead className="text-xs w-[140px]">Order #</TableHead>
                            <TableHead className="text-xs">Title</TableHead>
                            <TableHead className="text-xs">Status</TableHead>
                            <TableHead className="text-xs">Scope</TableHead>
                            <TableHead className="text-xs">Priority</TableHead>
                            <TableHead className="text-xs">Vendor</TableHead>
                            <TableHead className="text-xs text-right">Amount</TableHead>
                            <TableHead className="text-xs">Date</TableHead>
                            <TableHead className="text-xs w-[60px]"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredOrders.map((o) => (
                            <TableRow key={o.id} className="cursor-pointer hover:bg-muted/30" onClick={() => setDetailOrder(o)}>
                              <TableCell>
                                <code className="text-xs font-mono text-secondary">{o.order_number}</code>
                              </TableCell>
                              <TableCell>
                                <p className="text-sm font-medium text-foreground truncate max-w-[200px]">{o.title}</p>
                                <p className="text-[10px] text-muted-foreground">{o.requested_by_name}</p>
                              </TableCell>
                              <TableCell>
                                <Badge className={`${STATUS_CONFIG[o.status]?.color || ""} text-[10px]`}>{STATUS_CONFIG[o.status]?.label || o.status}</Badge>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                  {o.scope === "vessel" ? <><Ship className="w-3 h-3" />{o.vessel_name}</> : <><Building2 className="w-3 h-3" />Corporate</>}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge className={`${PRIORITY_CONFIG[o.priority]?.color || ""} text-[10px]`}>{PRIORITY_CONFIG[o.priority]?.label || o.priority}</Badge>
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground">{o.vendor_name || "—"}</TableCell>
                              <TableCell className="text-right text-sm font-semibold text-foreground">${Number(o.estimated_total || 0).toLocaleString()}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{format(new Date(o.created_at), "MMM d")}</TableCell>
                              <TableCell>
                                <Eye className="w-4 h-4 text-muted-foreground" />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="px-4 py-2 border-t border-border bg-muted/30 text-xs text-muted-foreground">
                      Showing {filteredOrders.length} of {orders.length} orders
                    </div>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* ─── APPROVALS TAB ─────────────────────────────────────────── */}
          <TabsContent value="approvals" className="space-y-4 mt-4">
            {(() => {
              const userAcocRole = EMPLOYEE_ROLE_TO_ACOC[employeePermissions.employee_role] || "vessel_crew";
              const myApprovals = orders.filter((o) => {
                if (o.status !== "pending_approval") return false;
                const est = Number(o.estimated_total || 0);
                const tier = getApprovalTier(est, o.order_type as "material" | "service", o.scope as "corporate" | "vessel");
                return canApprove(userAcocRole, tier);
              });
              const otherPending = orders.filter((o) => {
                if (o.status !== "pending_approval") return false;
                const est = Number(o.estimated_total || 0);
                const tier = getApprovalTier(est, o.order_type as "material" | "service", o.scope as "corporate" | "vessel");
                return !canApprove(userAcocRole, tier);
              });

              return (
                <div className="space-y-6">
                  {/* Your Authority Info */}
                  <div className="bg-secondary/5 border border-secondary/20 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center">
                        <UserCheck className="w-5 h-5 text-secondary" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-foreground">Your ACOC Authority: {ACOC_ROLE_LABELS[userAcocRole]}</p>
                        <p className="text-xs text-muted-foreground">
                          {userAcocRole === "ceo" ? "Full approval authority — all tiers" :
                           userAcocRole === "coo" ? "Track A lead — Tiers A2–A4 approval authority" :
                           userAcocRole === "cco" ? "Track B lead — Tiers B1–B2 sole signatory" :
                           userAcocRole === "tech_supt" ? "Tier A1 approval, A2 review & QES preparation" :
                           userAcocRole === "ops_manager" ? "Tier A1 approval, PR review" :
                           userAcocRole === "finance" ? "Budget gate — no direct approval authority" :
                           userAcocRole === "dpa_quality" ? "Audit access — no approval authority" :
                           "Initiate purchase requisitions only"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Orders requiring your approval */}
                  {myApprovals.length > 0 ? (
                    <div className="space-y-3">
                      <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                        <Clock className="w-4 h-4 text-amber-500" />
                        Awaiting Your Approval ({myApprovals.length})
                      </h3>
                      {myApprovals.map((o) => {
                        const est = Number(o.estimated_total || 0);
                        const tier = getApprovalTier(est, o.order_type as "material" | "service", o.scope as "corporate" | "vessel");
                        return (
                          <Card key={o.id} className="border-amber-500/30 cursor-pointer hover:border-secondary/50 transition-colors" onClick={() => { setDetailOrder(o); setTab("orders"); }}>
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between">
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <code className="text-xs font-mono text-secondary bg-secondary/10 px-2 py-0.5 rounded">{o.order_number}</code>
                                    <Badge variant="outline" className="text-[10px] font-mono border-secondary/30 text-secondary">{tier.id}</Badge>
                                    <Badge variant="outline" className="text-[10px]">
                                      {o.scope === "vessel" ? o.vessel_name : "Corporate"}
                                    </Badge>
                                  </div>
                                  <h4 className="text-sm font-semibold text-foreground">{o.title}</h4>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    By {o.requested_by_name} · {format(new Date(o.created_at), "MMM d, yyyy")}
                                    {o.vendor_name && ` · ${o.vendor_name}`}
                                  </p>
                                  <p className="text-[10px] text-muted-foreground mt-1">{tier.llcaNote}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xl font-bold text-foreground">${est.toLocaleString()}</p>
                                  <p className="text-[10px] text-muted-foreground">{formatValueRange(tier)}</p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  ) : (
                    <Card className="border-border">
                      <CardContent className="py-12 text-center">
                        <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-emerald-500/30" />
                        <p className="text-muted-foreground">No orders pending your approval</p>
                        <p className="text-xs text-muted-foreground/60 mt-1">Orders matching your authority level will appear here</p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Other pending orders (visibility) */}
                  {otherPending.length > 0 && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        Other Pending Orders ({otherPending.length})
                      </h3>
                      {otherPending.map((o) => {
                        const est = Number(o.estimated_total || 0);
                        const tier = getApprovalTier(est, o.order_type as "material" | "service", o.scope as "corporate" | "vessel");
                        return (
                          <Card key={o.id} className="border-border opacity-60">
                            <CardContent className="p-3 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <code className="text-xs font-mono text-muted-foreground">{o.order_number}</code>
                                <span className="text-xs text-muted-foreground">{o.title}</span>
                                <Badge variant="outline" className="text-[10px]">{tier.id}</Badge>
                              </div>
                              <span className="text-sm font-semibold text-muted-foreground">${est.toLocaleString()}</span>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </TabsContent>

          {/* ─── INVENTORY TAB ──────────────────────────────────────────── */}
          <TabsContent value="inventory" className="space-y-4 mt-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Track stock levels across warehouse and vessels</p>
              <Dialog open={invOpen} onOpenChange={setInvOpen}>
                <DialogTrigger asChild>
                  <Button onClick={() => toast.success("Created: Add Item", { description: "New entry added." })} className="gap-2 bg-secondary hover:bg-secondary/90 text-secondary-foreground"><Plus className="w-4 h-4" />Add Item</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Add Inventory Item</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label>Name</Label>
                      <Input value={invName} onChange={(e) => setInvName(e.target.value)} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Part Number</Label>
                        <Input value={invPartNum} onChange={(e) => setInvPartNum(e.target.value)} />
                      </div>
                      <div>
                        <Label>Category</Label>
                        <Select value={invCategory} onValueChange={setInvCategory}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">General</SelectItem>
                            <SelectItem value="spare_parts">Spare Parts</SelectItem>
                            <SelectItem value="safety_equipment">Safety Equipment</SelectItem>
                            <SelectItem value="consumables">Consumables</SelectItem>
                            <SelectItem value="tools">Tools</SelectItem>
                            <SelectItem value="electronics">Electronics</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Location</Label>
                        <Select value={invLocType} onValueChange={setInvLocType}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="warehouse">Warehouse</SelectItem>
                            <SelectItem value="vessel">Vessel</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {invLocType === "vessel" && (
                        <div>
                          <Label>Vessel</Label>
                          <Select value={invVesselId} onValueChange={setInvVesselId}>
                            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                              {vessels.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label>Qty On Hand</Label>
                        <Input type="number" value={invQty} onChange={(e) => setInvQty(Number(e.target.value))} />
                      </div>
                      <div>
                        <Label>Min Qty</Label>
                        <Input type="number" value={invMinQty} onChange={(e) => setInvMinQty(Number(e.target.value))} />
                      </div>
                      <div>
                        <Label>Unit</Label>
                        <Input value={invUnit} onChange={(e) => setInvUnit(e.target.value)} />
                      </div>
                    </div>
                    <Button onClick={() => createInventoryItem.mutate()} disabled={!invName.trim()} className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground">Add Item</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {invLoading ? (
              <div className="text-center py-12 text-muted-foreground">Loading inventory...</div>
            ) : inventory.length === 0 ? (
              <Card className="border-border">
                <CardContent className="py-12 text-center">
                  <Warehouse className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
                  <p className="text-muted-foreground">No inventory items yet</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="text-xs">Item Name</TableHead>
                        <TableHead className="text-xs">Part #</TableHead>
                        <TableHead className="text-xs">Category</TableHead>
                        <TableHead className="text-xs">Location</TableHead>
                        <TableHead className="text-xs text-right">On Hand</TableHead>
                        <TableHead className="text-xs text-right">Min Qty</TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {inventory.map((item) => {
                        const isLow = Number(item.quantity_on_hand) <= Number(item.minimum_quantity);
                        return (
                          <TableRow key={item.id} className={isLow ? "bg-amber-500/5" : ""}>
                            <TableCell>
                              <p className="text-sm font-medium text-foreground">{item.name}</p>
                            </TableCell>
                            <TableCell className="text-sm font-mono text-muted-foreground">{item.part_number || "—"}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-[10px]">{item.category}</Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {item.location_type === "vessel" ? item.vessel_name : "Warehouse"}
                            </TableCell>
                            <TableCell className={`text-sm text-right font-semibold ${isLow ? "text-amber-600 dark:text-amber-400" : "text-foreground"}`}>
                              {item.quantity_on_hand} {item.unit}
                            </TableCell>
                            <TableCell className="text-sm text-right text-muted-foreground">{item.minimum_quantity} {item.unit}</TableCell>
                            <TableCell>
                              {isLow ? (
                                <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300 text-[10px] gap-1">
                                  <AlertTriangle className="w-3 h-3" />Low Stock
                                </Badge>
                              ) : (
                                <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 text-[10px]">In Stock</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                <div className="px-4 py-2 border-t border-border bg-muted/30 text-xs text-muted-foreground">
                  {inventory.length} items · {lowStockItems.length} low stock
                </div>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </EmployeeLayout>
  );
};

export default EmployeeSupplyChain;
